<template>
  <div class="na-page na-staff">
    <StaffHeader role="Staff" />

    <main class="st-wrap st-below">
      <div class="st-head">
        <div>
          <span class="st-kicker">My settings</span>
          <h1 class="st-title">{{ displayName || 'Your profile' }} <span v-if="isCEO" class="st-badge brass ceo"><v-icon size="14">mdi-crown-outline</v-icon>CEO</span></h1>
          <p class="st-sub">Keep your profile current — your name and phone fill in automatically on the outreach templates you send.</p>
        </div>
      </div>

      <div class="layout">
        <!-- Sidebar -->
        <aside class="side">
          <section class="st-card me">
            <div class="avatar-wrap">
              <span class="avatar" :style="photoURL ? { backgroundImage: `url(${photoURL})` } : {}">{{ photoURL ? '' : initials }}</span>
              <button type="button" class="edit" :disabled="uploading" title="Change photo" @click="$refs.avatarInput.click()"><v-icon size="16" color="#14100A">{{ uploading ? 'mdi-loading mdi-spin' : 'mdi-camera-outline' }}</v-icon></button>
              <input ref="avatarInput" type="file" accept="image/*" hidden @change="updateAvatar" />
            </div>
            <b class="name">{{ displayName || 'Team member' }}</b>
            <small class="st-muted">{{ email }}</small>
            <small v-if="title" class="st-muted">{{ title }}</small>
          </section>

          <section class="st-card points">
            <div class="row"><span class="st-kpi-label">Your points</span><span class="st-badge green">+{{ pointsToday }} today</span></div>
            <div class="big">{{ points.toLocaleString() }}</div>
            <div class="st-bar"><div :style="{ width: progressPercent + '%' }"></div></div>
            <small class="st-muted">{{ nextBadge ? `${pointsToNextBadge} points to ${nextBadge.name}` : 'Every points badge earned' }}<template v-if="stats.streakDays"> · {{ stats.streakDays }}-day streak</template></small>
            <div class="badges">
              <span v-for="b in BADGES" :key="b.name" class="st-badge" :class="b.test(stats) ? 'navy' : 'off'" :title="b.test(stats) ? 'Earned' : 'Not yet'">{{ b.name }}</span>
            </div>
          </section>

          <section class="st-card activity">
            <h3 class="st-card-title"><v-icon size="20">mdi-chart-timeline-variant</v-icon>Your last 14 days</h3>
            <div v-if="!activityLoaded" class="center"><div class="st-loader"></div></div>
            <p v-else-if="!totalActivity" class="st-muted">Log a call, email or meeting on a lead to see your activity here.</p>
            <template v-else>
              <svg viewBox="0 0 280 80" preserveAspectRatio="none" class="spark" aria-hidden="true">
                <polyline :points="activityPoints" fill="none" stroke="#14315E" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" vector-effect="non-scaling-stroke" />
              </svg>
              <div class="counts">
                <div><b>{{ activity.call }}</b><small>Calls</small></div>
                <div><b>{{ activity.email }}</b><small>Emails</small></div>
                <div><b>{{ activity.meeting }}</b><small>Meetings</small></div>
                <div><b>{{ activity.demo }}</b><small>Demos</small></div>
              </div>
            </template>
          </section>
        </aside>

        <!-- Content -->
        <section class="st-card content">
          <div class="st-seg tabs">
            <button type="button" :class="{ on: tab === 'profile' }" @click="tab = 'profile'"><v-icon size="18">mdi-account-outline</v-icon>Profile</button>
            <button type="button" :class="{ on: tab === 'security' }" @click="tab = 'security'"><v-icon size="18">mdi-lock-outline</v-icon>Password</button>
          </div>

          <form v-if="tab === 'profile'" class="st-stack form" @submit.prevent="saveProfile">
            <div class="st-grid-2">
              <div class="st-field"><label>Full name</label><input v-model="profileForm.fullName" placeholder="Your full name" /></div>
              <div class="st-field"><label>Title</label><input v-model="profileForm.title" placeholder="Account Executive" /></div>
            </div>
            <div class="st-field"><label>Email</label><input :value="email" disabled /><small class="st-muted">Your sign-in email can't be changed here. Ask an admin if it needs to change.</small></div>
            <div class="st-field"><label>Phone</label><input v-model="profileForm.phone" type="tel" placeholder="(555) 123-4567" /><small class="st-muted">Used for [Your Phone] in email templates.</small></div>
            <div class="st-field"><label>Mailing address</label><textarea v-model="profileForm.address" rows="3" placeholder="Where we should send your materials"></textarea></div>
            <div class="row-end"><button type="submit" class="st-btn primary" :disabled="savingProfile">{{ savingProfile ? 'Saving…' : 'Save changes' }}</button></div>
          </form>

          <form v-else class="st-stack form" @submit.prevent="changePassword">
            <div class="st-note">Use at least 8 characters. A longer password with a mix of letters, numbers and symbols is strongest.</div>
            <div class="st-field">
              <label>Current password</label>
              <div class="pw"><input v-model="passwordForm.currentPassword" :type="showCurrent ? 'text' : 'password'" autocomplete="current-password" /><button type="button" class="st-icon-btn" @click="showCurrent = !showCurrent"><v-icon size="18">{{ showCurrent ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon></button></div>
            </div>
            <div class="st-field">
              <label>New password</label>
              <div class="pw"><input v-model="passwordForm.newPassword" :type="showNew ? 'text' : 'password'" autocomplete="new-password" /><button type="button" class="st-icon-btn" @click="showNew = !showNew"><v-icon size="18">{{ showNew ? 'mdi-eye-off-outline' : 'mdi-eye-outline' }}</v-icon></button></div>
              <template v-if="passwordForm.newPassword">
                <div class="st-bar strength"><div :style="{ width: strength.percent + '%', background: strength.color }"></div></div>
                <small :style="{ color: strength.color }">{{ strength.label }}</small>
              </template>
            </div>
            <div class="st-field"><label>Confirm new password</label><input v-model="passwordForm.confirmPassword" :type="showNew ? 'text' : 'password'" autocomplete="new-password" /></div>
            <div v-if="passwordError" class="st-alert">{{ passwordError }}</div>
            <div class="row-end">
              <button type="button" class="st-btn ghost" :disabled="sendingReset" @click="sendReset">{{ sendingReset ? 'Sending…' : 'Forgot it? Email me a link' }}</button>
              <button type="submit" class="st-btn primary" :disabled="changingPassword">{{ changingPassword ? 'Changing…' : 'Change password' }}</button>
            </div>
          </form>
        </section>
      </div>
    </main>

    <div v-if="toast" class="st-toast">{{ toast }}<button type="button" @click="toast = ''">✕</button></div>
  </div>
</template>

<script>
import StaffHeader from '@/components/StaffHeader.vue'
import { auth, db, functions, storage } from '@/firebase'
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword, updateProfile } from 'firebase/auth'
import { collectionGroup, doc, getDoc, getDocs, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { getDownloadURL, ref as sref, uploadBytes } from 'firebase/storage'
import { BADGES } from '@/salesConfig'
import { fnError } from '@/billingConfig'
import '@/assets/na-staff.css'

// Points badges in order, from salesConfig (e.g. Rookie 50, Prospector 250, Rainmaker 1000).
const POINT_TIERS = BADGES
  .map(b => ({ name: b.name, threshold: [50, 250, 1000, 2500, 5000, 10000].find(n => b.test({ points: n }) && !b.test({ points: n - 1 })) }))
  .filter(t => t.threshold)
  .sort((a, b) => a.threshold - b.threshold)
const dayKey = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`

export default {
  name: 'StaffManageSettingsView',
  components: { StaffHeader },
  data: () => ({
    BADGES, tab: 'profile', uid: '', email: '', displayName: '', title: '', photoURL: '', userType: '',
    stats: { points: 0, todayPoints: 0, todayKey: '', streakDays: 0, wins: 0, demos: 0 },
    unsubStats: null, uploading: false,
    activity: { call: 0, email: 0, meeting: 0, demo: 0 }, activityDays: [], activityLoaded: false,
    profileForm: { fullName: '', title: '', phone: '', address: '' }, savingProfile: false,
    passwordForm: { currentPassword: '', newPassword: '', confirmPassword: '' },
    showCurrent: false, showNew: false, changingPassword: false, passwordError: '', sendingReset: false, toast: '',
  }),
  computed: {
    isCEO() { return String(this.userType || '').toLowerCase() === 'ceo' },
    initials() { return String(this.displayName || this.email || 'NA').replace(/@.*/, '').split(/[\s._-]+/).filter(Boolean).map(p => p[0]).join('').slice(0, 2).toUpperCase() },
    points() { return Number(this.stats.points) || 0 },
    pointsToday() { return this.stats.todayKey === dayKey() ? Number(this.stats.todayPoints) || 0 : 0 },
    nextBadge() { return POINT_TIERS.find(t => this.points < t.threshold) || null },
    pointsToNextBadge() { return this.nextBadge ? this.nextBadge.threshold - this.points : 0 },
    progressPercent() {
      if (!this.nextBadge) return 100
      const prev = [...POINT_TIERS].reverse().find(t => t.threshold <= this.points)
      const from = prev ? prev.threshold : 0
      return Math.min(100, Math.round(((this.points - from) / (this.nextBadge.threshold - from)) * 100))
    },
    totalActivity() { return Object.values(this.activity).reduce((a, b) => a + b, 0) },
    activityPoints() {
      const d = this.activityDays
      if (d.length < 2) return ''
      const max = Math.max(1, ...d)
      return d.map((v, i) => `${(i / (d.length - 1)) * 260 + 10},${70 - (v / max) * 60}`).join(' ')
    },
    strength() {
      const p = this.passwordForm.newPassword
      let s = 0
      if (p.length >= 8) s += 25
      if (p.length >= 12) s += 25
      if (/[a-z]/.test(p) && /[A-Z]/.test(p)) s += 25
      if (/[0-9]/.test(p)) s += 12.5
      if (/[^a-zA-Z0-9]/.test(p)) s += 12.5
      return s >= 75 ? { percent: s, label: 'Strong', color: '#2F7D46' } : s >= 50 ? { percent: s, label: 'Medium', color: '#B8925A' } : { percent: s, label: 'Weak', color: '#A31F1F' }
    },
  },
  async mounted() {
    const u = auth.currentUser
    if (!u) return
    this.uid = u.uid
    this.email = u.email || ''
    this.displayName = u.displayName || ''
    this.photoURL = u.photoURL || ''
    try {
      const s = await getDoc(doc(db, 'users', u.uid))
      if (s.exists()) {
        const d = s.data()
        this.displayName = d.fullName || [d.firstName, d.lastName].filter(Boolean).join(' ') || d.displayName || this.displayName
        this.title = d.title || ''
        this.photoURL = d.profilePhotoURL || this.photoURL
        this.userType = d.type || ''
        this.profileForm = { fullName: this.displayName, title: d.title || '', phone: d.phone || '', address: d.address || '' }
      } else {
        this.profileForm.fullName = this.displayName
      }
    } catch (e) { console.error(e) }
    this.unsubStats = onSnapshot(doc(db, 'user_stats', u.uid), s => { if (s.exists()) this.stats = { ...this.stats, ...s.data() } }, e => console.warn(e))
    this.loadActivity()
  },
  beforeDestroy() { if (this.unsubStats) this.unsubStats() },
  methods: {
    notify(m) { this.toast = m; clearTimeout(this._t); this._t = setTimeout(() => { this.toast = '' }, 3000) },
    async loadActivity() {
      try {
        const snap = await getDocs(query(collectionGroup(db, 'interactions'), where('by', '==', this.uid)))
        const counts = { call: 0, email: 0, meeting: 0, demo: 0 }
        const keys = Array.from({ length: 14 }, (_, i) => dayKey(new Date(Date.now() - (13 - i) * 864e5)))
        const buckets = Object.fromEntries(keys.map(k => [k, 0]))
        snap.forEach(d => {
          const x = d.data()
          if (counts[x.type] !== undefined) counts[x.type]++
          const at = x.at && (x.at.toDate ? x.at.toDate() : new Date(x.at.seconds * 1000))
          if (at && buckets[dayKey(at)] !== undefined) buckets[dayKey(at)]++
        })
        this.activity = counts
        this.activityDays = keys.map(k => buckets[k])
      } catch (e) { console.warn('Activity not loaded (an index may be needed — see the console link):', e) } finally { this.activityLoaded = true }
    },
    async updateAvatar(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) { this.notify('Choose an image under 5 MB.'); return }
      this.uploading = true
      try {
        const r = sref(storage, `user-photos/${this.uid}/${Date.now()}_${file.name.replace(/[^\w.-]+/g, '_')}`)
        await uploadBytes(r, file, { contentType: file.type })
        const url = await getDownloadURL(r)
        await updateProfile(auth.currentUser, { photoURL: url })
        await setDoc(doc(db, 'users', this.uid), { profilePhotoURL: url, updatedAt: serverTimestamp() }, { merge: true })
        this.photoURL = url
        this.notify('Photo updated')
      } catch (err) { this.notify('Could not update your photo: ' + fnError(err)) } finally { this.uploading = false }
    },
    async saveProfile() {
      this.savingProfile = true
      try {
        const f = this.profileForm
        await updateProfile(auth.currentUser, { displayName: f.fullName.trim() })
        await setDoc(doc(db, 'users', this.uid), { fullName: f.fullName.trim(), title: f.title.trim(), phone: f.phone.trim(), address: f.address.trim(), updatedAt: serverTimestamp() }, { merge: true })
        this.displayName = f.fullName.trim()
        this.title = f.title.trim()
        this.notify('Profile saved')
      } catch (e) { this.notify('Could not save: ' + fnError(e)) } finally { this.savingProfile = false }
    },
    async changePassword() {
      this.passwordError = ''
      const f = this.passwordForm
      if (!f.currentPassword || !f.newPassword) { this.passwordError = 'Fill in your current and new password.'; return }
      if (f.newPassword.length < 8) { this.passwordError = 'The new password needs at least 8 characters.'; return }
      if (f.newPassword !== f.confirmPassword) { this.passwordError = "The new passwords don't match."; return }
      this.changingPassword = true
      try {
        const u = auth.currentUser
        await reauthenticateWithCredential(u, EmailAuthProvider.credential(u.email, f.currentPassword))
        await updatePassword(u, f.newPassword)
        this.passwordForm = { currentPassword: '', newPassword: '', confirmPassword: '' }
        this.notify('Password changed')
      } catch (e) {
        const code = (e && e.code) || ''
        this.passwordError = /wrong-password|invalid-credential/.test(code) ? 'Your current password is incorrect.'
          : /too-many-requests/.test(code) ? 'Too many attempts. Wait a few minutes and try again.'
            : /weak-password/.test(code) ? 'Choose a stronger password.' : "Your password didn't change. Try again."
      } finally { this.changingPassword = false }
    },
    async sendReset() {
      this.sendingReset = true
      try {
        await httpsCallable(functions, 'sendPasswordResetEmail')({ email: this.email })
        this.notify(`Reset link sent to ${this.email}`)
      } catch (e) { this.notify("The reset email didn't send: " + fnError(e)) } finally { this.sendingReset = false }
    },
  },
}
</script>

<style scoped>
.ceo { vertical-align: middle; margin-left: 8px; font-family: var(--na-sans); font-size: 0.75rem; }
.layout { display: grid; grid-template-columns: 330px minmax(0, 1fr); gap: 24px; align-items: start; }
.side { display: flex; flex-direction: column; gap: 18px; position: sticky; top: 96px; }
.me { display: flex; flex-direction: column; align-items: center; gap: 4px; text-align: center; }
.avatar-wrap { position: relative; margin-bottom: 10px; }
.avatar { width: 92px; height: 92px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background: var(--na-navy); color: #fff; font-family: var(--na-serif); font-weight: 700; font-size: 1.9rem; background-size: cover; background-position: center; border: 3px solid #fff; box-shadow: 0 0 0 1px var(--na-rule); }
.edit { position: absolute; right: -2px; bottom: -2px; width: 32px; height: 32px; border-radius: 50%; border: 2px solid #fff; background: var(--na-brass); cursor: pointer; display: flex; align-items: center; justify-content: center; }
.name { font-family: var(--na-serif); font-size: 1.25rem; }
.points { display: flex; flex-direction: column; gap: 8px; }
.points .row { display: flex; justify-content: space-between; align-items: center; }
.big { font-family: var(--na-serif); font-weight: 700; font-size: 2.3rem; line-height: 1; }
.badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.st-badge.off { background: #F4F1E9; color: #A8A08F; }
.activity { display: flex; flex-direction: column; gap: 12px; }
.activity .st-card-title { font-size: 1.05rem; }
.spark { width: 100%; height: 80px; }
.counts { display: grid; grid-template-columns: repeat(4, 1fr); text-align: center; border-top: 1px solid var(--na-rule); padding-top: 10px; }
.counts div { display: flex; flex-direction: column; }
.counts b { font-family: var(--na-serif); font-size: 1.3rem; }
.counts small { font-size: 0.75rem; color: var(--na-ink-2); }
.center { display: flex; justify-content: center; padding: 12px; }
.tabs { margin-bottom: 22px; }
.tabs button { display: inline-flex; align-items: center; gap: 6px; }
.form { max-width: 620px; }
.pw { display: flex; gap: 8px; align-items: center; }
.pw input { flex: 1; }
.strength { height: 5px; margin-top: 6px; }
.row-end { display: flex; justify-content: flex-end; gap: 10px; flex-wrap: wrap; }
@media (max-width: 1000px) {
  .layout { grid-template-columns: 1fr; }
  .side { position: static; }
}
</style>